import { updatePlaidCreditCardTransaction } from '../../../graphql/custom_mutations';
import { generateClient } from 'aws-amplify/api';

const client = generateClient();

/**
 * Link a receipt to a Plaid transaction, but only if the transaction is free.
 *
 * "Free" means the transaction has no receipt yet, or already has THIS receipt
 * (so re-saving the same pairing, and publishing it, keep working). The check
 * runs inside DynamoDB as part of the write, so two people pairing different
 * receipts with the same transaction at the same moment cannot both succeed,
 * and a publish can never silently replace another receipt's link.
 *
 * Every place that writes capitalOneCreditCardTransactionReceiptId onto a
 * transaction should go through this instead of updateTransaction.
 *
 * @param {object} args
 * @param {string} args.transactionId  SpkPlaidCreditCardTransactionTbl id
 * @param {string} args.receiptId      SpkCapitalOneCreditCardTransactionReceiptTbl id
 * @param {object} [args.fields]       Other fields to write in the same update
 *                                     (status, approvedById, notification id, ...)
 * @returns {Promise<{ok: true, id: string} | {ok: false, reason: 'TAKEN'|'ERROR', error?: any}>}
 */
export async function claimTransactionForReceipt({ transactionId, receiptId, fields = {} }) {
  if (!transactionId || !receiptId) {
    return { ok: false, reason: 'ERROR', error: new Error('transactionId and receiptId are required') };
  }

  const input = {
    ...fields,
    id: transactionId,
    capitalOneCreditCardTransactionReceiptId: receiptId,
  };

  // Free = never set, set to null, set to an empty string, or already this receipt.
  const condition = {
    or: [
      { capitalOneCreditCardTransactionReceiptId: { attributeExists: false } },
      { capitalOneCreditCardTransactionReceiptId: { attributeType: '_null' } },
      { capitalOneCreditCardTransactionReceiptId: { eq: '' } },
      { capitalOneCreditCardTransactionReceiptId: { eq: receiptId } },
    ],
  };

  try {
    const response = await client.graphql({
      query: updatePlaidCreditCardTransaction,
      variables: { input, condition },
    });
    if (response?.errors?.length) {
      console.error('⚠️ claimTransactionForReceipt returned partial errors:', JSON.stringify(response.errors, null, 2));
    }
    return { ok: true, id: response?.data?.updateSpkPlaidCreditCardTransactionTbl?.id };
  } catch (err) {
    const errors = err?.errors || [];
    const taken = errors.some(e =>
      String(e?.errorType || '').includes('ConditionalCheckFailed') ||
      String(e?.message || '').includes('conditional request failed')
    );
    if (taken) {
      console.warn('claimTransactionForReceipt: transaction already has a different receipt', { transactionId, receiptId });
      return { ok: false, reason: 'TAKEN' };
    }
    console.error('❌ claimTransactionForReceipt FAILED:', { message: err?.message, errors, raw: err });
    return { ok: false, reason: 'ERROR', error: err };
  }
}

/** Plain-language message for a failed claim, for toasts and modals. */
export function describeClaimFailure(result) {
  if (result?.reason === 'TAKEN') {
    return 'This transaction already has a different receipt attached. Choose a different transaction, or ask a card manager to review it.';
  }
  return 'The receipt could not be linked to the transaction. Please try again in a few minutes.';
}
